/* ------------------------------------------------------------ Settings */

/**
 * Real Halloween checkout link. While null, every buy button scrolls to the
 * offer block instead of leaving the page — no wrong links are published.
 */
export const CHECKOUT_URL: string | null = 'https://halloween-party.impultienda.ar/checkout'

export const PRICE = '$22'
export const PRICE_VALUE = 22

export const BRAND_COMPANY = 'Redondo Land'
export const PRODUCT_NAME = 'Halloween Kids Ultimate Party Kit'
export const STICKY_CTA_LABEL = 'GET THE KIT — $22'

/* -------------------------------------------------------------- Images */

export type ImageAsset = {
  src: string
  width: number
  height: number
  alt: string
}

const W = 1536
const H = 1024

const IMG_PLANNER =
  'https://cdn.magicpatterns.com/uploads/hfdMJSZzLNGBXCfRbERQm7/Planificador_de_fiesta_de_Halloween_acogedor.png'
const IMG_RECIPE_CARDS =
  'https://cdn.magicpatterns.com/uploads/67PhSMjqtwrdQZWCmiAugN/Tarjetas_de_recetas_monstruosamente_deliciosas.png'
const IMG_EBOOK =
  'https://cdn.magicpatterns.com/uploads/4A9tWkfiHiRgMLk8dV46c5/Recetario_de_Halloween__Monstruos_y_Tentempies.png'
const IMG_ACTIVITY_KIT =
  'https://cdn.magicpatterns.com/uploads/oz6H2sQLMroVpe2548nncz/Kit_de_actividades_imprimibles_de_Halloween.png'
const IMG_PUZZLES =
  'https://cdn.magicpatterns.com/uploads/fKkb335ucoxJU8nDwG5KNz/Fichas_infantiles_de_Halloween_con_colores_y_actividades.png'
const IMG_GAMES_KIT =
  'https://cdn.magicpatterns.com/uploads/focM2JkATeFZRMAGihunz2/Kit_imprimible_de_juegos_de_Halloween.png'
const IMG_MAIN =
  'https://cdn.magicpatterns.com/uploads/9cJxTweBg5ym1W9sF5nPP9/mock_up_principal_(2).png'

/** Main bundle mockup — used in the hero and the main offer only. */
export const MAIN_MOCKUP: ImageAsset = {
  src: IMG_MAIN,
  width: W,
  height: H,
  alt: 'Halloween Kids Ultimate Party Kit — printable Bingo, Memory Match, playing cards, word search, maze, coloring pages and the Monster Munchies recipe ebook',
}

/* ---------------------------------------------------------------- Hero */

export const HERO_EYEBROW = 'HALLOWEEN IS ALMOST HERE!'
export const HERO_HEADLINE: string[] = [
  'The Costumes Are Ready.',
  'The Candy Is Bought.',
  'But What Will the Kids Actually Do?',
]
export const HERO_SUB: string[] = [
  'Skip the last-minute search for Halloween games.',
  'Get 6 printable game collections, 4 creative activity collections, and 3 special bonuses — ready to download, print, and bring to the party.',
]
export const HERO_VALUE_LINE = 'Less planning. More laughter. More Halloween memories.'
export const HERO_PRICE_LABEL = 'HALLOWEEN LAUNCH PRICE'
export const HERO_CTA = 'GET MY HALLOWEEN KIT — $22 →'
export const heroMicro: string[] = ['Instant Digital Download', 'Print at Home', 'No Physical Shipping']

/* -------------------------------------------------------- Problem */

export const PAIN_HEADLINE: string[] = ['You Wanted a Fun Halloween.', 'Not Another Week of Planning.']

export const PAIN_BODY: string[] = [
  'You want the kids laughing, playing, and enjoying every moment.',
  "But between costumes, decorations, candy, food, and everything else, there's one thing that's easy to leave until the last minute:",
]
export const PAIN_EMPHASIS = 'Keeping everyone entertained.'
export const PAIN_SEARCH =
  "And suddenly, you're searching for games, saving random printables, and hoping you've prepared enough."
export const PAIN_WORRY_LEAD = 'The worry?'
export const PAIN_WORRY_QUOTE = '“What happens when the first game ends and everyone wants something else to do?”'

export const SOLUTION_HEADLINE = 'What If the Activities Were Already Taken Care Of?'
export const SOLUTION_BODY: string[] = [
  "That's where the Halloween Kids Ultimate Party Kit comes in.",
  'A ready-to-print collection of games, puzzles, coloring activities, and spooky extras — so you can spend less time preparing and more time enjoying the celebration.',
]
export const SOLUTION_CLOSING: string[] = ['You make the memories.', "We've helped with the activity planning."]

/* --------------------------------------------------------- Collections */

export const COLLECTIONS_TITLE = 'All the Halloween Fun, Ready to Print.'
export const COLLECTIONS_SUB =
  '6 game collections. 4 creative activity collections. Plenty of ways to celebrate.'
export const GAMES_LABEL = '6 HALLOWEEN GAME COLLECTIONS'
export const ACTIVITIES_LABEL = '4 CREATIVE ACTIVITY COLLECTIONS'

export type Collection = { name: string; count: string; description?: string }

export const gameCollections: Collection[] = [
  { name: 'HALLOWEEN BINGO', count: '30 unique boards and themed calling cards.', description: 'A spooky twist on a party favorite.' },
  { name: 'MEMORY MATCH', count: '36 matching pairs.', description: 'Flip, remember, and find the matching Halloween pictures.' },
  { name: 'HALLOWEEN CHARADES', count: '48 action prompts.', description: 'Get everyone acting, guessing, and laughing.' },
  { name: 'PICTURE DOMINOES', count: '55 printable domino pieces.', description: 'Match the Halloween pictures and build the chain.' },
  { name: 'HALLOWEEN TRIVIA', count: '60 multiple-choice questions.', description: 'Bring a little friendly competition to the party.' },
  { name: 'HALLOWEEN PLAYING CARDS', count: '52 playing cards + 2 jokers.', description: 'A vintage-inspired Halloween deck for familiar card games.' },
]

export const activityCollections: Collection[] = [
  { name: 'WORD SEARCHES', count: '10 printable Halloween word searches.' },
  { name: 'MAZES', count: '10 fun Halloween maze challenges.' },
  { name: 'CROSSWORDS', count: '10 spooky crossword puzzles.' },
  { name: 'COLORING PAGES', count: '15 Halloween illustrations to color.' },
]

export const COLLECTIONS_CLOSING =
  "From lively group games to quieter creative activities, there's something for different moments of the celebration."
export const COLLECTIONS_CTA = 'GET THE COMPLETE KIT — $22'

/* -------------------------------------------------------- How it works */

export const STEPS_TITLE = 'Halloween Fun in 3 Simple Steps.'

export type Step = { title: string; body: string }

export const steps: Step[] = [
  { title: 'Download', body: 'Get instant access to your digital Halloween collection.' },
  { title: 'Print & Prepare', body: 'Choose your games, print the pages, and cut the pieces where needed.' },
  { title: 'Play & Celebrate', body: 'Bring the activities to the table and enjoy the Halloween fun.' },
]

export const STEPS_FOOTNOTE = 'Basic printing supplies are required. Lamination is optional.'

/* ---------------------------------------------------------- Look inside */

export const INSIDE_TITLE = "See What's Actually Inside Your Halloween Kit."
export const INSIDE_SUB =
  "Real printable games, creative activities, and spooky extras — so you know exactly what you're getting."

export type InsidePage = { caption: string; image: ImageAsset }

/** Shown whole (never cropped). Max five, no duplicates. */
export const insidePages: InsidePage[] = [
  {
    caption: 'Bingo, Charades, Dominoes & Trivia',
    image: {
      src: IMG_GAMES_KIT,
      width: W,
      height: H,
      alt: 'Printable Halloween Bingo boards, Charades prompt cards, picture Dominoes and Trivia question cards',
    },
  },
  {
    caption: 'Word Searches, Mazes, Crosswords & Coloring',
    image: {
      src: IMG_PUZZLES,
      width: W,
      height: H,
      alt: 'Printable Halloween word search, maze, crossword and coloring page',
    },
  },
  {
    caption: 'Games & Activities Together',
    image: {
      src: IMG_ACTIVITY_KIT,
      width: W,
      height: H,
      alt: 'Printed Halloween game sheets, puzzles, coloring page and cut-out domino pieces laid out together',
    },
  },
]

/* ------------------------------------------------------------- Bonuses */

export const BONUS_EYEBROW = '3 SPECIAL BONUSES INCLUDED'
export const BONUS_TITLE = "And Halloween Isn't Just About the Games…"
export const BONUS_SUB = 'Make the snacks more fun and the final preparations a little easier.'

export type Bonus = {
  label: string
  title: string
  copy: string[]
  highlights: string[]
  emotional?: string
  image: ImageAsset
}

export const bonuses: Bonus[] = [
  {
    label: 'BONUS 1 — MONSTER MUNCHIES & SPOOKY SNACKS',
    title: 'Turn Ordinary Snacks Into Halloween Magic.',
    copy: [
      'Imagine the kids decorating spooky pancake faces, making mummy-inspired treats, or creating their own little monster snacks.',
      "Inside this 40-page recipe ebook, you'll discover 15 playful Halloween recipes made with familiar ingredients.",
    ],
    highlights: ['15 Halloween Recipes', 'Easy-to-Follow Ideas', 'Fun Family Moments'],
    emotional: 'Sometimes the sweetest Halloween memories happen in the kitchen.',
    image: {
      src: IMG_EBOOK,
      width: W,
      height: H,
      alt: 'Bonus 1: Monster Munchies & Spooky Snacks recipe ebook with sample recipe pages',
    },
  },
  {
    label: 'BONUS 2 — PRINTABLE RECIPE CARDS',
    title: 'Keep the Recipes Close. Keep the Fun Going.',
    copy: [
      "No more switching between tabs while you're cooking.",
      'Get 15 printable recipe cards with ingredients and simple preparation steps, ready to keep nearby in the kitchen.',
    ],
    highlights: ['15 Printable Cards', 'Ingredients', 'Simple Instructions'],
    image: {
      src: IMG_RECIPE_CARDS,
      width: W,
      height: H,
      alt: 'Bonus 2: printable Monster Munchies recipe cards with ingredients and steps',
    },
  },
  {
    label: 'BONUS 3 — 48-HOUR HALLOWEEN PLANNING CHECKLIST',
    title: 'Less Last-Minute Scrambling. More Time to Celebrate.',
    copy: [
      'Halloween preparations can pile up quickly.',
      'This 34-page guide helps you organize the final 48 hours, from preparing your printable activities to checking supplies and setting up your party space.',
    ],
    highlights: ['34-Page Guide', 'Clear Preparation Steps', 'Final Party Checklist'],
    image: {
      src: IMG_PLANNER,
      width: W,
      height: H,
      alt: 'Bonus 3: 48-Hour Halloween Planning Checklist with contents and chapter pages',
    },
  },
]

export const BONUS_CLOSING = 'All 3 bonuses are included with your Halloween Kids Ultimate Party Kit.'

/* ---------------------------------------------------------- Main offer */

export const OFFER_EYEBROW = 'YOUR COMPLETE HALLOWEEN KIT'
export const OFFER_TITLE: string[] = ['Everything Together.', 'One Less Thing to Worry About.']
export const OFFER_URGENCY = 'Halloween Is October 31. Get Your Games Ready Before the Big Day.'
export const OFFER_URGENCY_SUB =
  'Why spend another evening searching for activities when you could already have them ready to print?'

export type OfferItem = { title: string; detail: string }

export const offerItems: OfferItem[] = [
  { title: '6 Printable Game Collections', detail: 'Bingo, Memory Match, Charades, Dominoes, Trivia & Playing Cards.' },
  { title: '4 Creative Activity Collections', detail: 'Word Searches, Mazes, Crosswords & Coloring Pages.' },
  {
    title: '3 Special Bonuses',
    detail: 'Monster Munchies Recipe Ebook, Printable Recipe Cards & 48-Hour Planning Guide.',
  },
]

export const OFFER_BADGE = 'HALLOWEEN LAUNCH SPECIAL'
export const OFFER_PRICE_LINE =
  'One Complete Digital Kit. 10 Printable Game & Activity Collections. 3 Special Bonuses.'
export const OFFER_CTA = 'YES! GET MY COMPLETE KIT — $22 →'
export const offerMicro: string[] = ['Instant Digital Download', 'One-Time Purchase', 'No Physical Shipping']
export const OFFER_CLOSING = 'Get the activities ready today. Enjoy the moments when Halloween arrives.'

/* ---------------------------------------------------------- Comparison */

export const COMPARISON_TITLE = 'Less Searching. More Celebrating.'
export const COMPARISON_WITHOUT = 'WITHOUT THE KIT'
export const COMPARISON_WITH = 'WITH THE KIT'

export type ComparisonRow = { without: string; with: string }

export const comparisonRows: ComparisonRow[] = [
  { without: 'Searching different websites', with: 'Printable collections together in one place' },
  { without: 'Wondering what to prepare next', with: 'Different games and activities to choose from' },
  { without: 'Hunting for spooky snack ideas', with: '15 themed recipes included' },
  { without: 'Remembering every last-minute detail', with: 'A 48-hour planning guide to follow' },
]

export const COMPARISON_CLOSING: string[] = [
  'The celebration is still yours.',
  "The activity planning doesn't have to start from zero.",
]

/* ----------------------------------------------------------------- FAQ */

export const FAQ_TITLE = 'Still Have a Few Questions?'

export type Faq = { question: string; answer: string }

export const faqs: Faq[] = [
  {
    question: 'Is this a physical product?',
    answer: 'No. This is a digital collection of printable PDF files. Nothing is shipped.',
  },
  {
    question: 'What exactly is included?',
    answer: 'Six game collections, four creative activity collections, and three special Halloween bonuses.',
  },
  {
    question: 'Do I need a printer?',
    answer: 'Yes, to use the materials as printables. Paper and scissors may also be needed. Lamination is optional.',
  },
  {
    question: 'What ages is it for?',
    answer:
      "The kit includes a variety of visual games, coloring activities, and reading-based puzzles. Adult help may be needed depending on each child's age.",
  },
  {
    question: 'Do I have to print everything?',
    answer: 'No. Choose only the activities you want to use.',
  },
  {
    question: 'Are the three bonuses included in the $22 price?',
    answer: 'Yes. All three bonuses are included in the complete digital kit.',
  },
  {
    question: 'How do I get my files?',
    answer: "You'll receive access to the downloadable files through the purchase delivery process.",
  },
]

/* --------------------------------------------------------------- Close */

export const CLOSING_EYEBROW = 'HALLOWEEN IS OCTOBER 31'
export const CLOSING_TITLE: string[] = ['When Halloween Arrives…', 'Be Ready for the Fun.']
export const CLOSING_LINES: string[] = [
  'The costumes will come out.',
  'The excitement will build.',
  'And the kids will be ready for something fun.',
]
export const CLOSING_TAIL =
  "Make sure the activities are one thing you don't have to figure out at the last minute."

export const FINAL_PRODUCT = 'Halloween Kids Ultimate Party Kit'
export const FINAL_SUBLINE = '6 Game Collections + 4 Creative Activity Collections + 3 Bonuses'
export const FINAL_PRICE_LABEL = 'HALLOWEEN LAUNCH PRICE'
export const FINAL_CTA = 'GET INSTANT ACCESS — $22 →'
export const finalMicro: string[] = ['Printable PDFs', 'Instant Digital Access', 'No Physical Shipping']
export const FINAL_LINE = 'More fun. Less planning. Halloween memories worth making.'

/* -------------------------------------------------------------- Footer */

export const footerTagline: string[] = ['Creative ideas.', 'Practical printables.', 'More moments worth sharing.']

/** Links with href null are hidden. Add the real approved Refund Policy URL to show it. */
export type FooterLink = { label: string; href: string | null }

export const footerLinks: FooterLink[] = [
  { label: 'Privacy Policy', href: '#' },
  { label: 'Terms of Purchase', href: '#' },
  { label: 'Contact', href: '#' },
  { label: 'Refund Policy', href: null },
]
